import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    Button,
    Space,
    Tag,
    Modal,
    message,
    Tooltip,
    Alert,
    Typography,
    Spin,
    Empty
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ReloadOutlined,
    GlobalOutlined,
    SafetyCertificateOutlined,
    ThunderboltOutlined,
    EyeOutlined,
    CopyOutlined,
    LinkOutlined,
    ClearOutlined
} from '@ant-design/icons';
import cdnService from '../../services/cdnService';
import CdnDomainStatusTag from '../../../shared/components/CdnDomainStatusTag';

import { userPath } from '../../../shared/config/paths';
import { formatDateTime } from '../../../shared/utils/date';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import RenewAction from '../../../shared/components/RenewAction';
import RowActions, { useRowContextMenu } from '../../../shared/components/RowActions';

const { Text } = Typography;

const Domains = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cnameInfoVisible, setCnameInfoVisible] = useState(false);
    const [currentCnameInfo, setCurrentCnameInfo] = useState(null);
    const [cnameLoading, setCnameLoading] = useState(false);
    const [purgingId, setPurgingId] = useState(null);

    useEffect(() => {
        loadDomains();
    }, []);

    const loadDomains = async () => {
        setLoading(true);
        try {
            const response = await cdnService.getDomains();
            if (response.success) {
                setDomains(response.data || []);
            } else {
                message.error(response.error || t('Failed to load domains'));
            }
        } catch (error) {
            message.error(t('Failed to load domains: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    const goToDetail = (record) => {
        navigate(userPath(`/cdn/domains/${record.domain}`));
    };

    const handleDelete = (id) => {
        // 套餐下最后一个域名:删除后套餐随之结束(后端 OrderService::resourceDeleted)
        const sub = domains.find((d) => d.id === id)?.subscription_id;
        const lastOnPlan = sub && domains.filter((d) => d.subscription_id === sub).length === 1;
        Modal.confirm({
            title: t('Confirm deletion'),
            content: lastOnPlan
                ? t('This is the last domain on its plan. Deleting it also ends the plan. This action cannot be undone.')
                : t('Delete this domain? This action cannot be undone.'),
            okText: t('OK'),
            cancelText: t('Cancel'),
            okType: 'danger',
            onOk: async () => {
                try {
                    const response = await cdnService.deleteDomain(id);
                    if (response.success) {
                        message.success(t('Deleted successfully'));
                        loadDomains();
                    } else {
                        message.error(response.error || t('Deletion failed'));
                    }
                } catch (error) {
                    message.error(t('Deletion failed: {{message}}', { message: error.message }));
                }
            }
        });
    };

    const handlePurgeCache = (record) => {
        Modal.confirm({
            title: t('Purge cache'),
            content: t('Purge all cache for domain {{domain}}? The first request after the purge will fetch content from the origin.', { domain: record.domain }),
            okText: t('Clear'),
            cancelText: t('Cancel'),
            onOk: async () => {
                setPurgingId(record.id);
                try {
                    const response = await cdnService.purgeCache(record.id);
                    if (response.success) {
                        message.success(t('Cache purge request submitted'));
                    } else {
                        message.error(response.error || t('Failed to purge cache'));
                    }
                } catch (error) {
                    message.error(t('Failed to purge cache: {{message}}', { message: error.message }));
                } finally {
                    setPurgingId(null);
                }
            }
        });
    };

    const fetchCnameInfo = async (domainRecord) => {
        if (!domainRecord?.id) {
            message.info(t('Please save the domain and origin configuration first'));
            return;
        }

        setCnameLoading(true);
        try {
            const response = await cdnService.getHostnames(domainRecord.id);
            if (response.success) {
                const hostnameList = (response.data || []).map((h) => ({
                    key: h.id || h.hostname,
                    hostname: h.hostname,
                    cnameTarget: h.cname_target
                }));

                setCurrentCnameInfo({
                    domain: domainRecord.domain,
                    hostnames: hostnameList
                });
                setCnameInfoVisible(true);
            } else {
                message.error(response.error || t('Failed to retrieve CNAME information'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve CNAME information: {{message}}', { message: error.message }));
        } finally {
            setCnameLoading(false);
        }
    };

    const handleViewCname = (record) => {
        fetchCnameInfo(record);
    };

    const copyCnameToClipboard = (text) => {
        if (!text) {
            return;
        }
        // 兼容非 HTTPS 环境
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(() => {
                message.success(t('Copied to clipboard'));
            }).catch(() => {
                message.error(t('Failed to copy. Please copy manually.'));
            });
        } else {
            // Fallback for HTTP
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-9999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                message.success(t('Copied to clipboard'));
            } catch (err) {
                message.error(t('Failed to copy. Please copy manually.'));
            }
            document.body.removeChild(textArea);
        }
    };

    const cnameColumns = [
        {
            title: t('Hostname'),
            dataIndex: 'hostname',
            key: 'hostname',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: t('CNAME target'),
            key: 'cnameTarget',
            render: (_, record) => record.cnameTarget ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
                        <Text code style={{ whiteSpace: 'nowrap', display: 'block' }}>{record.cnameTarget}</Text>
                    </div>
                    <Button
                        type="link"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => copyCnameToClipboard(record.cnameTarget)}
                        style={{ flexShrink: 0 }}
                    >{t('Copy')}</Button>
                </div>
            ) : (
                <Text type="secondary">{t('Generation pending')}</Text>
            )
        },
    ];

    const renderCnameModalBody = () => {
        if (cnameLoading) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin />
                </div>
            );
        }

        if (!currentCnameInfo) {
            return (
                <Alert
                    message={t('No CNAME information yet')}
                    type="info"
                    showIcon
                />
            );
        }

        const list = currentCnameInfo.hostnames || [];

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Alert
                    message={t('DNS setup guide')}
                    description={t('Add CNAME records for the following hosts to complete setup. Built-in DNS syncs automatically, with no manual action needed.')}
                    type="info"
                    showIcon
                />

                <Space>
                    <Text strong>{t('Domain:')}</Text>
                    <Tag color="blue">{currentCnameInfo.domain}</Tag>
                </Space>

                {list.length === 0 ? (
                    <Alert
                        message={t('No origin configured yet')}
                        description={t('Please add the hosts you want to accelerate in Origin settings first, then view their CNAME records.')}
                        type="warning"
                        showIcon
                    />
                ) : (
                    <Table
                        columns={cnameColumns}
                        dataSource={list}
                        pagination={false}
                        size="small"
                        rowKey={(row) => row.key || row.hostname}
                        scroll={{ x: 'max-content' }}
                    />
                )}

                <Alert
                    message={t('Operation notice')}
                    description={
                        <ol style={{ paddingLeft: 20, margin: 0 }}>
                            <li style={{ marginBottom: 8 }}>{t('Create a CNAME record for the corresponding hostname at your DNS provider.')}</li>
                            <li style={{ marginBottom: 8 }}>{t('Select CNAME as the record type and use the target from the table above as the record value.')}</li>
                            <li style={{ marginBottom: 0 }}>{t('DNS changes may take a few minutes to take effect. Please wait.')}</li>
                        </ol>
                    }
                    type="success"
                    showIcon
                />
            </Space>
        );
    };

    const getRowActions = (record) => [
        { key: 'detail', label: t('View details'), icon: <EyeOutlined />, onClick: () => goToDetail(record) },
        { key: 'purge', label: t('Purge cache'), icon: <ClearOutlined />, inline: true, loading: purgingId === record.id, onClick: () => handlePurgeCache(record) },
        { key: 'cname', label: t('View CNAME'), icon: <LinkOutlined />, onClick: () => handleViewCname(record) },
        { key: 'delete', label: t('Delete'), icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(record.id) },
    ];
    const rowMenu = useRowContextMenu(getRowActions);

    const columns = [
        {
            title: t('Domains'),
            dataIndex: 'domain',
            key: 'domain',
            render: (text) => (
                <Space>
                    <GlobalOutlined />
                    <strong>{text}</strong>
                </Space>
            )
        },
        {
            title: t('Origin'),
            dataIndex: 'hostname_count',
            key: 'hostname_count',
            render: (count) => (
                <span>{count || 0}{t(' origins')}</span>
            )
        },
        {
            title: t('Plan'),
            key: 'plan',
            onCell: () => ({ onClick: (e) => e.stopPropagation() }),
            render: (_, record) => (
                <Space size={4}>
                    {record.plan?.name ? <Tag>{record.plan.name}</Tag> : <Tag color="default">{t('Not bound')}</Tag>}
                    {record.cname_switch && <Tooltip title={t('Please change the CNAME to {{to}}', { to: record.cname_switch.to })}><Tag color="orange">{t('CNAME switch pending')}</Tag></Tooltip>}
                    <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate(userPath(record.plan ? `/cdn/domains/${record.domain}/plan` : `/cdn/plans?domain_id=${record.id}`))}>{record.plan ? t('Plan') : t('Select a plan')}</Button>
                </Space>
            )
        },
        {
            title: 'SSL',
            dataIndex: 'ssl_enabled',
            key: 'ssl_enabled',
            render: (enabled) => !!enabled ? (
                <Tag icon={<SafetyCertificateOutlined />} color="success">{t('Enabled')}</Tag>
            ) : (
                <Tag color="default">{t('Disabled')}</Tag>
            )
        },
        {
            title: 'WAF',
            dataIndex: 'waf_enabled',
            key: 'waf_enabled',
            render: (enabled) => !!enabled ? (
                <Tag icon={<ThunderboltOutlined />} color="processing">{t('Enabled')}</Tag>
            ) : (
                <Tag color="default">{t('Disabled')}</Tag>
            )
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <Space size={4}>
                    <CdnDomainStatusTag status={status} />
                    {record.verified === false && <Tag color="orange">{t('Pending verification')}</Tag>}
                </Space>
            )
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => formatDateTime(text)
        },
        {
            title: t('Expires'),
            key: 'billing',
            width: 170,
            render: (_, record) => <RenewAction billing={record.billing} resourceName={record.domain} />,
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => <RowActions actions={getRowActions(record)} />
        }
    ];

    return (
        <div>
            <Card
                title={t('CDN domains')}
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadDomains}
                            loading={loading}
                        >{t('Refresh')}</Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate(userPath('/cdn/domains/new'))}
                        >{t('Add domain')}</Button>
                    </Space>
                }
            >
                <Table
                    onRow={rowMenu.onRow(rowLink(navigate, (r) => `/cdn/domains/${r.domain}`))}
                    rowClassName={() => ROW_LINK_CLASS}
                    columns={columns}
                    dataSource={domains}
                    rowKey="id"
                    loading={loading}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={t('No CDN domains yet')}
                            >
                                <Space>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => navigate(userPath('/cdn/domains/new'))}
                                    >{t('Add your first domain')}</Button>
                                </Space>
                            </Empty>
                        )
                    }}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => t('{{total}} domains total', { total })
                    }}
                />
            </Card>

            {rowMenu.menu}

            {/* CNAME配置信息Modal */}
            <Modal
                title={<Space><LinkOutlined />{t(' CNAME setup guide')}</Space>}
                open={cnameInfoVisible}
                onCancel={() => {
                    setCnameInfoVisible(false);
                    setCurrentCnameInfo(null);
                }}
                footer={[
                    <Button
                        key="close"
                        onClick={() => {
                            setCnameInfoVisible(false);
                            setCurrentCnameInfo(null);
                        }}
                    >{t('Close')}</Button>
                ]}
                width="90vw"
                style={{ maxWidth: 760 }}
            >
                {renderCnameModalBody()}
            </Modal>
        </div>
    );
};

export default Domains;
